import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { buttonVariants } from "@/components/ui/button";

const iconButtonSizeVariants = cva("vadium-hit-target inline-flex items-center justify-center shrink-0 rounded-md", {
  variants: {
    size: {
      sm: "size-[var(--icon-button-sm)] [&_svg]:size-3.5",
      md: "size-[var(--icon-button-md)] [&_svg]:size-4",
      lg: "size-[var(--icon-button-lg)] [&_svg]:size-5"
    }
  },
  defaultVariants: { size: "md" }
});

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonSizeVariants> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  label: string;
  showTooltip?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant = "ghost", size, label, showTooltip = true, children, ...props }, ref) => {
    const button = (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
          buttonVariants({ variant, size: null }),
          iconButtonSizeVariants({ size }),
          className
        )}
        {...props}
      >
        {children}
      </button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonSizeVariants };
